import React, { useEffect, useMemo, useRef, useState } from "react";
import Layout from "../Layout/Layout";
import { APIUrl } from "../../Helper/APIUrl";
import { useSnackbar } from "notistack";
import { Button } from "react-bootstrap";
import DataTable from "react-data-table-component";

function ManageEnquiry() {
    const token = JSON.parse(localStorage.getItem("tcn"));
    const { enqueueSnackbar } = useSnackbar();

    const [search, setSearch] = useState("");
    const [selectedDate, setSelectedDate] = useState("");
    const [enquiryList, setEnquiryList] = useState([]);
    const [activeFilter, setActiveFilter] = useState("ALL");
    const [selectedEnquiry, setSelectedEnquiry] = useState(null);
    const [updatingReadIds, setUpdatingReadIds] = useState([]);
    const detailsSectionRef = useRef(null);

    const enquiryURL = APIUrl.Enquiry;

    const parseJSON = async (response) => {
        try {
            const text = await response.text();
            if (!text) return null;
            return JSON.parse(text);
        } catch (err) {
            console.warn("Failed to parse JSON response", err);
            return null;
        }
    };

    const getAllEnquiry = async () => {
        if (!token) {
            enqueueSnackbar("Authentication token not found", { variant: "error" });
            return;
        }

        try {
            const response = await fetch(enquiryURL + "GetAllEnquiry", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({}),
            });

            const result = await parseJSON(response);

            if (result && result.Message === "Success" && Array.isArray(result.data)) {
                setEnquiryList(result.data);
            } else {
                setEnquiryList([]);
                enqueueSnackbar("No enquiry records found", { variant: "info" });
            }
        } catch (error) {
            setEnquiryList([]);
            enqueueSnackbar("Error loading enquiry list: " + error.message, {
                variant: "error",
            });
        }
    };

    useEffect(() => {
        getAllEnquiry();
    }, []);

    const showDetails = (row) => {
        setSelectedEnquiry(row);
        setTimeout(() => {
            detailsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 0);
    };

    const closeDetails = () => {
        setSelectedEnquiry(null);
    };

    const getEnquiryId = (row) => row?.EnquiryId ?? row?.Id ?? row?.enquiryId ?? row?.id;

    const updateIsReadAdminEnquiry = async (row, shouldMarkRead) => {
        const enquiryId = getEnquiryId(row);

        if (!token) {
            enqueueSnackbar("Authentication token not found", { variant: "error" });
            return;
        }

        if (!enquiryId) {
            enqueueSnackbar("Unable to identify enquiry id", { variant: "error" });
            return;
        }

        try {
            setUpdatingReadIds((prev) => [...prev, enquiryId]);

            const response = await fetch(
                `${enquiryURL}UpdateIsReadAdminEnquiry/${enquiryId}`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({
                        EnquiryId: enquiryId,
                        Value: shouldMarkRead ? "readed" : "unread",
                    }),
                }
            );

            const result = await parseJSON(response);

            if (!response.ok) {
                throw new Error(result?.Message || "Failed to update read status");
            }

            setEnquiryList((prev) =>
                prev.map((item) => {
                    const itemId = getEnquiryId(item);
                    if (itemId === enquiryId) {
                        return { ...item, IsReadAdmin: shouldMarkRead };
                    }
                    return item;
                })
            );

            setSelectedEnquiry((prev) => {
                if (!prev) return prev;
                const selectedId = getEnquiryId(prev);
                if (selectedId === enquiryId) {
                    return { ...prev, IsReadAdmin: shouldMarkRead };
                }
                return prev;
            });

            enqueueSnackbar(
                shouldMarkRead
                    ? "Enquiry marked as read"
                    : "Enquiry marked as unread",
                { variant: "success" }
            );
        } catch (error) {
            enqueueSnackbar("Failed to update read status: " + error.message, {
                variant: "error",
            });
        } finally {
            setUpdatingReadIds((prev) => prev.filter((id) => id !== enquiryId));
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        const date = new Date(dateString);
        const day = String(date.getDate()).padStart(2, '0');
        const month = date.toLocaleString('en-US', { month: 'short' });
        const year = date.getFullYear();
        return `${day}-${month}-${year}`;
    };

    const formatValue = (value) => {
        if (value === null || value === undefined || value === "") return "N/A";
        return value;
    };

    const formatPrice = (priceValue) => {
        if (priceValue === null || priceValue === undefined) return "N/A";
        return Number(priceValue).toLocaleString("en-IN");
    };

    const getISODate = (dateValue) => {
        return new Date(dateValue).toISOString().split("T")[0];
    };

    const todayDate = getISODate(new Date());
    const yesterdayDate = getISODate(new Date(Date.now() - 24 * 60 * 60 * 1000));

    const filteredEnquiryList = useMemo(() => {
        let result = [...enquiryList];

        if (activeFilter === "CALL") {
            result = result.filter((x) => x.IsForCall === true);
        } else if (activeFilter === "WHATSAPP") {
            result = result.filter((x) => x.IsForWhatsapp === true);
        } else if (activeFilter === "ENQUIRY_ONLY") {
            result = result.filter(
                (x) =>
                    x.IsForEnquiry === true && x.IsForCall !== true && x.IsForWhatsapp !== true
            );
        } else if (activeFilter === "TODAY") {
            result = result.filter((x) => x.CreatedOn && getISODate(x.CreatedOn) === todayDate);
        } else if (activeFilter === "YESTERDAY") {
            result = result.filter((x) => x.CreatedOn && getISODate(x.CreatedOn) === yesterdayDate);
        } else if (activeFilter === "READ") {
            result = result.filter((x) => x.IsReadAdmin === true);
        } else if (activeFilter === "UNREAD") {
            result = result.filter((x) => x.IsReadAdmin !== true);
        }

        // Apply date filter if selected
        if (selectedDate) {
            result = result.filter((x) => {
                if (!x.CreatedOn) return false;
                const createdDate = new Date(x.CreatedOn).toISOString().split('T')[0];
                return createdDate === selectedDate;
            });
        }

        if (!search.trim()) return result;

        const query = search.toLowerCase();
        return result.filter(
            (x) =>
                (x.Name && x.Name.toLowerCase().includes(query)) ||
                (x.Email && x.Email.toLowerCase().includes(query)) ||
                (x.Mobile && x.Mobile.toLowerCase().includes(query)) ||
                (x.Message && x.Message.toLowerCase().includes(query)) ||
                (x.PropertyTitle && x.PropertyTitle.toLowerCase().includes(query)) ||
                (x.PropertyGeneratedId && x.PropertyGeneratedId.toLowerCase().includes(query)) ||
                (x.VendorName && x.VendorName.toLowerCase().includes(query)) ||
                (x.ClientName && x.ClientName.toLowerCase().includes(query))
        );
    }, [activeFilter, enquiryList, search, selectedDate]);

    const totalCount = enquiryList.length;
    const callCount = enquiryList.filter((x) => x.IsForCall === true).length;
    const whatsappCount = enquiryList.filter((x) => x.IsForWhatsapp === true).length;
    const enquiryOnlyCount = enquiryList.filter(
        (x) => x.IsForEnquiry === true && x.IsForCall !== true && x.IsForWhatsapp !== true
    ).length;
    const todayCount = enquiryList.filter(
        (x) => x.CreatedOn && getISODate(x.CreatedOn) === todayDate
    ).length;
    const yesterdayCount = enquiryList.filter(
        (x) => x.CreatedOn && getISODate(x.CreatedOn) === yesterdayDate
    ).length;
    const readCount = enquiryList.filter((x) => x.IsReadAdmin === true).length;
    const unreadCount = enquiryList.filter((x) => x.IsReadAdmin !== true).length;

    const cardClass =
        "rounded-lg p-6 border cursor-pointer transition-shadow hover:shadow-lg";

    const columns = [
        {
            name: <strong>SNo.</strong>,
            selector: (row, index) => index + 1,
            width: "75px",
        },

        {
            name: <strong>Client Enquiry </strong>,
            cell: (row) => (<>
                <div className="flex flex-col gap-1 py-2">
                    <div className="text-sm">{row.Name || "N/A"}</div>
                    <div className="text-sm">{row.Email || "N/A"}</div>
                    <div className="text-sm text-gray-600">{row.Mobile || "N/A"}</div>
                    <div className="flex flex-wrap gap-1 py-2">
                        {row.IsForCall === true && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                Calling
                            </span>
                        )}
                        {row.IsForWhatsapp === true && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                Whatsapp
                            </span>
                        )}
                        {row.IsForEnquiry === true && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                Enquiry
                            </span>
                        )}
                        {row.IsForCall !== true && row.IsForWhatsapp !== true && row.IsForEnquiry !== true && (
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                N/A
                            </span>
                        )}
                    </div>
                </div>
            </>
            ),
            sortable: true,
            grow: 1.6,
        },
        {
            name: <strong>Interested in Property</strong>,
            cell: (row) => (
                <div className="flex flex-col gap-1 py-2">

                    <div className="text-sm text-large">Property ID: {formatValue(row.PropertyGeneratedId)}</div>
                    <div className="text-sm font-medium">{formatValue(row.PropertyLabelName)}</div>
                    <div className="text-xs text-gray-600">{formatValue(row.PropertyTitle)}</div>
                    <div className="text-xs text-gray-600">{formatValue(row.Address)}, {formatValue(row.City)}, {formatValue(row.State)}</div>
                </div>
            ),
            sortable: true,
            reorder: true,
            grow: 1.7,
        },
        {
            name: <strong>Property Posted by (Vendor)</strong>,
            cell: (row) => (
                <div className="flex flex-wrap gap-1 py-2">
                    <div className="text-sm font-medium">{formatValue(row.VendorName)}</div>
                    <div className="text-sm">{formatValue(row.VendorEmail)}</div>
                    <div className="text-sm text-gray-600">{formatValue(row.VendorNumber)}</div>

                </div>
            ),
            grow: 1.7,
        },
        {
            name: <strong>Enquiry Date</strong>,
            selector: (row) => row.CreatedOn ? formatDate(row.CreatedOn) : "N/A",
            sortable: true,
            grow: 1.2,
        },
        {
            name: <strong>Mark as Read/Unread</strong>,
            cell: (row) => (
                <div>
                    {(() => {
                        const enquiryId = getEnquiryId(row);
                        const isUpdating = updatingReadIds.includes(enquiryId);
                        const isRead = row.IsReadAdmin === true;

                        return (
                            <Button
                                onClick={() => updateIsReadAdminEnquiry(row, !isRead)}
                                disabled={isUpdating}
                                className={`text-white font-medium rounded-lg text-sm px-3 py-2 ${
                                    isRead
                                        ? "bg-gradient-to-r from-amber-500 via-amber-600 to-amber-700"
                                        : "bg-gradient-to-r from-emerald-500 via-emerald-600 to-emerald-700"
                                }`}
                                style={{ border: "none", minWidth: "120px" }}
                            >
                                {isUpdating ? "Updating..." : isRead ? "Mark Unread" : "Mark Read"}
                            </Button>
                        );
                    })()}
                </div>
            ),
        },
        // {
        //     name: <strong>Status</strong>,
        //     cell: (row) => (
        //         <span
        //             className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
        //                 row.IsReadAdmin === true
        //                     ? "bg-green-100 text-green-800"
        //                     : "bg-gray-100 text-gray-700"
        //             }`}
        //         >
        //             {row.IsReadAdmin === true ? "Read" : "Unread"}
        //         </span>
        //     ),
        //     grow: 0.8,
        // },
        {
            name: <strong>Action</strong>,
            cell: (row) => (
                <div>
                    <Button
                        onClick={() => showDetails(row)}
                        className="text-white bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 hover:bg-gradient-to-br focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm px-3 py-2"
                        style={{ border: "none" }}
                    >
                        <i className="fa fa-eye" aria-hidden="true"></i> Details
                    </Button>
                </div>
            ),
        },
    ];

    const conditionalRowStyles = [
        {
            when: (row) => row.IsReadAdmin === true,
            style: {
                backgroundColor: "#fffdf2",
            },
        },
        {
            when: (row) => row.IsReadAdmin !== true,
            style: {
                backgroundColor: "#ffffff",
            },
        },
    ];

    return (
        <div>
            <Layout>
                <main className="flex-1 overflow-y-auto p-4 sm:p-6 lg:p-8">
                    <div className="space-y-6">
                        <div>
                            <h2 className="text-xl font-semibold text-gray-900 mb-6">
                                Enquiry Overview
                            </h2>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
                            <div
                                onClick={() => setActiveFilter("ALL")}
                                className={`${cardClass} bg-gradient-to-br from-slate-50 to-slate-100 border-slate-200 ${activeFilter === "ALL" ? "ring-2 ring-slate-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-slate-800">Total Enquiry</p>
                                        <p className="text-3xl font-bold text-slate-700 mt-2">{totalCount}</p>
                                    </div>
                                    <div className="text-4xl text-slate-400">📋</div>
                                </div>
                            </div>
                            <div
                                onClick={() => {
                                    setActiveFilter("TODAY");
                                    setSelectedDate("");
                                }}
                                className={`${cardClass} bg-gradient-to-br from-cyan-50 to-cyan-100 border-cyan-200 ${activeFilter === "TODAY" ? "ring-2 ring-cyan-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-cyan-800">Today Enquiry</p>
                                        <p className="text-3xl font-bold text-cyan-600 mt-2">{todayCount}</p>
                                    </div>
                                    <div className="text-4xl text-cyan-400">📆</div>
                                </div>
                            </div>

                            <div
                                onClick={() => {
                                    setActiveFilter("YESTERDAY");
                                    setSelectedDate("");
                                }}
                                className={`${cardClass} bg-gradient-to-br from-rose-50 to-rose-100 border-rose-200 ${activeFilter === "YESTERDAY" ? "ring-2 ring-rose-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-rose-800">Yesterday Enquiry</p>
                                        <p className="text-3xl font-bold text-rose-600 mt-2">{yesterdayCount}</p>
                                    </div>
                                    <div className="text-4xl text-rose-400">🗓️</div>
                                </div>
                            </div>

                            <div
                                onClick={() => setActiveFilter("CALL")}
                                className={`${cardClass} bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 ${activeFilter === "CALL" ? "ring-2 ring-blue-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-blue-800">For  Calling</p>
                                        <p className="text-3xl font-bold text-blue-600 mt-2">{callCount}</p>
                                    </div>
                                    <div className="text-4xl text-blue-400">📞</div>
                                </div>
                            </div>

                            <div
                                onClick={() => {
                                    setActiveFilter("READ");
                                    setSelectedDate("");
                                }}
                                className={`${cardClass} bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 ${activeFilter === "READ" ? "ring-2 ring-emerald-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-emerald-800">Readed Enquiry</p>
                                        <p className="text-3xl font-bold text-emerald-600 mt-2">{readCount}</p>
                                    </div>
                                    <div className="text-4xl text-emerald-400">✅</div>
                                </div>
                            </div>

                            <div
                                onClick={() => {
                                    setActiveFilter("UNREAD");
                                    setSelectedDate("");
                                }}
                                className={`${cardClass} bg-gradient-to-br from-orange-50 to-orange-100 border-orange-200 ${activeFilter === "UNREAD" ? "ring-2 ring-orange-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-orange-800">Unread Enquiry</p>
                                        <p className="text-3xl font-bold text-orange-600 mt-2">{unreadCount}</p>
                                    </div>
                                    <div className="text-4xl text-orange-400">⏳</div>
                                </div>
                            </div>

                            <div
                                onClick={() => setActiveFilter("WHATSAPP")}
                                className={`${cardClass} bg-gradient-to-br from-green-50 to-green-100 border-green-200 ${activeFilter === "WHATSAPP" ? "ring-2 ring-green-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-green-800">For Whatsapp Chat</p>
                                        <p className="text-3xl font-bold text-green-600 mt-2">{whatsappCount}</p>
                                    </div>
                                    <div className="text-4xl text-green-400">💬</div>
                                </div>
                            </div>

                            <div
                                onClick={() => setActiveFilter("ENQUIRY_ONLY")}
                                className={`${cardClass} bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 ${activeFilter === "ENQUIRY_ONLY" ? "ring-2 ring-amber-400" : ""
                                    }`}
                            >
                                <div className="flex items-center justify-between">
                                    <div>
                                        <p className="text-sm font-medium text-amber-800">For Enquiry</p>
                                        <p className="text-3xl font-bold text-amber-600 mt-2">{enquiryOnlyCount}</p>
                                    </div>
                                    <div className="text-4xl text-amber-400">📝</div>
                                </div>
                            </div>


                        </div>

                        {/* Details Section */}
                        {selectedEnquiry && (
                            <div ref={detailsSectionRef} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-semibold text-gray-900">
                                        Enquiry Details
                                    </h2>
                                    <Button
                                        onClick={() => closeDetails()}
                                        style={{ background: "transparent", border: "none", padding: "0" }}
                                    >
                                        <i className="fa fa-times fa-2x text-gray-600" aria-hidden="true"></i>
                                    </Button>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Enquiry Name
                                        </label>
                                        <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.Name)}</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Enquiry Email
                                        </label>
                                        <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.Email)}</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Enquiry Mobile
                                        </label>
                                        <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.Mobile)}</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Channel Type
                                        </label>
                                        <div className="bg-gray-50 px-4 py-3 rounded-lg border border-gray-200 flex flex-wrap gap-2">
                                            {selectedEnquiry.IsForCall === true && (
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                                    Call
                                                </span>
                                            )}
                                            {selectedEnquiry.IsForWhatsapp === true && (
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                                                    Whatsapp
                                                </span>
                                            )}
                                            {selectedEnquiry.IsForEnquiry === true && (
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                                                    Enquiry
                                                </span>
                                            )}
                                            {selectedEnquiry.IsForCall !== true && selectedEnquiry.IsForWhatsapp !== true && selectedEnquiry.IsForEnquiry !== true && (
                                                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-700">
                                                    N/A
                                                </span>
                                            )}
                                        </div>
                                    </div>

                                    <div className="md:col-span-2">
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Message
                                        </label>
                                        <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200 whitespace-pre-wrap">{formatValue(selectedEnquiry.Message)}</p>
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Created On
                                        </label>
                                        <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">
                                            {formatDate(selectedEnquiry.CreatedOn)}
                                        </p>
                                    </div>
                                </div>
                                <hr />
                                <div className="mt-8 mb-10">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Property Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Property Title</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.PropertyTitle)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Property Generated ID</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.PropertyGeneratedId)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.Address)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">City</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.City)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">State</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.State)}</p>
                                        </div>
                                        {/* <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.Latitude)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.Longitude)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Property Price</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatPrice(selectedEnquiry.PropertyPrice)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Year Built</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.YearBuilt)}</p>
                                        </div> */}
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Is Featured</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{selectedEnquiry.IsFeatured === null || selectedEnquiry.IsFeatured === undefined ? "N/A" : selectedEnquiry.IsFeatured ? "Yes" : "No"}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Project Type Name</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ProjectTypeName)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Property Label Name</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.PropertyLabelName)}</p>
                                        </div>
                                    </div>
                                </div>
                                <hr />

                                <div className="mt-8 mb-10">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Vendor Details</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor User Name</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorUserName)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Email</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorEmail)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Name</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorName)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Office Address</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorOfficeAddress)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Office Number</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorOfficeNumber)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Number</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorNumber)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Personal Address</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorPersonalAddress)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Facebook</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorFacebook)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor Twitter</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorTwitter)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Vendor LinkedIn</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.VendorLinkdin)}</p>
                                        </div>
                                    </div>
                                </div>
                                <hr />
                                <div className="mt-8">
                                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Details (If Client is already registered as a Vendor in AskMeReality)</h3>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client User Name</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientUserName)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Email</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientEmail)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Name</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientName)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Office Number</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientOfficeNumber)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Office Address</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientOfficeAddress)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Phone</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientPhone)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Personal Address</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientPersonalAddress)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Facebook</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientFacebook)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client Twitter</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientTwitter)}</p>
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-2">Client LinkedIn</label>
                                            <p className="text-gray-900 bg-gray-50 px-4 py-3 rounded-lg border border-gray-200">{formatValue(selectedEnquiry.ClientLinkdin)}</p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                            <div className="overflow-x-auto">
                                <DataTable
                                    title="Manage Enquiry"
                                    columns={columns}
                                    data={filteredEnquiryList}
                                    conditionalRowStyles={conditionalRowStyles}
                                    pagination
                                    paginationPerPage={10}
                                    paginationRowsPerPageOptions={[10, 25, 50]}
                                    fixedHeader
                                    fixedHeaderScrollHeight="550px"
                                    highlightOnHover
                                    subHeader
                                    subHeaderAlign="right"
                                    subHeaderComponent={
                                        <div className="flex flex-col sm:flex-row gap-4 w-full">
                                            <input
                                                type="text"
                                                placeholder="Search by name, email, phone, or message"
                                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                value={search}
                                                onChange={(e) => setSearch(e.target.value)}
                                            />
                                            <input
                                                type="date"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                            />
                                            {(search || selectedDate) && (
                                                <Button
                                                    onClick={() => { setSearch(""); setSelectedDate(""); }}
                                                    className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
                                                    style={{ border: "none", whiteSpace: "nowrap" }}
                                                >
                                                    Clear
                                                </Button>
                                            )}
                                        </div>
                                    }
                                />
                            </div>
                        </div>
                    </div>
                </main>
            </Layout>
        </div>
    );
}

export default ManageEnquiry;
